import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Save, Loader2, RotateCcw, User, Phone, Activity } from "lucide-react";
import CustomSelect from "../components/Common/CustomSelect";
import { getPatientById, createPatient, updatePatient } from "../services/patientService";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+44|0)?[0-9]{10,11}$/;

interface PatientFormData {
  forename: string;
  surname: string;
  nhs_number: string;
  date_of_birth: string;
  gender: string;
  hospital_number: string;
  mrn_number: string;
  contact_number: string;
  email: string;
  address: string;
  diagnosis: string;
}

const INITIAL_FORM: PatientFormData = {
  forename: "",
  surname: "",
  nhs_number: "",
  date_of_birth: "",
  gender: "",
  hospital_number: "",
  mrn_number: "",
  contact_number: "",
  email: "",
  address: "",
  diagnosis: "",
};

const PatientForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [form, setForm] = useState<PatientFormData>(INITIAL_FORM);
  const [savedForm, setSavedForm] = useState<PatientFormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [apiError, setApiError] = useState<string | null>(null);

  // Load existing patient details when in edit mode
  useEffect(() => {
    if (!isEdit || !id) return;
    setFetching(true);
    getPatientById(id)
      .then((patient) => {
        const loaded: PatientFormData = {
          forename: patient.forename || "",
          surname: patient.surname || "",
          nhs_number: patient.nhs_number || "",
          date_of_birth: patient.date_of_birth || "",
          gender: patient.gender || "",
          hospital_number: patient.hospital_number || "",
          mrn_number: patient.mrn_number || "",
          contact_number: patient.contact_number || "",
          email: patient.email || "",
          address: patient.address || "",
          diagnosis: typeof patient.diagnosis === "string" ? patient.diagnosis : (patient.diagnosis as any)?.condition || "",
        };
        setForm(loaded);
        setSavedForm(loaded);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || err?.message || "Failed to load patient details";
        toast.error(msg);
        navigate("/patients");
      })
      .finally(() => setFetching(false));
  }, [isEdit, id, navigate]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setApiError(null);
  };

  const handleReset = () => {
    setForm(isEdit ? savedForm : INITIAL_FORM);
    setErrors({});
    setApiError(null);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!form.forename.trim()) newErrors.forename = "First Name is required";
    if (!form.surname.trim()) newErrors.surname = "Last Name is required";

    if (!form.nhs_number.trim()) {
      newErrors.nhs_number = "NHS Number is required";
    }

    if (!form.date_of_birth) {
      newErrors.date_of_birth = "Date of birth is required";
    } else {
      const dobDate = new Date(form.date_of_birth);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (isNaN(dobDate.getTime())) {
        newErrors.date_of_birth = "Must be a valid date";
      } else if (dobDate > today) {
        newErrors.date_of_birth = "Date of birth cannot be in the future";
      }
    }

    if (!form.gender) newErrors.gender = "Gender is required";
    if (!form.hospital_number.trim()) newErrors.hospital_number = "Hospital number is required";
    if (!form.mrn_number.trim()) newErrors.mrn_number = "MRN number is required";

    if (form.contact_number.trim() && !PHONE_REGEX.test(form.contact_number.trim())) {
      newErrors.contact_number = "Enter a valid phone number (10-11 digits)";
    }

    if (form.email.trim() && !EMAIL_REGEX.test(form.email.trim())) {
      newErrors.email = "Enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      toast.error("Please correct the validation errors before submitting.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        forename: form.forename.trim(),
        surname: form.surname.trim(),
        nhs_number: form.nhs_number.trim(),
        date_of_birth: form.date_of_birth,
        gender: form.gender,
        hospital_number: form.hospital_number.trim(),
        mrn_number: form.mrn_number.trim(),
        contact_number: form.contact_number.trim() || null,
        email: form.email.trim() || null,
        address: form.address.trim() || null,
        diagnosis: form.diagnosis.trim() || null,
      };

      if (isEdit && id) {
        await updatePatient(id, payload);
        toast.success("Patient profile updated successfully!");
        navigate(`/patients/${id}`);
      } else {
        const response = await createPatient(payload);
        toast.success("Patient created successfully!");
        const newId = response?.data?.patient_id || response?.data?.id || response?.patient_id;
        if (newId) {
          navigate(`/patients/${newId}`);
        } else {
          navigate("/patients");
        }
      }
    } catch (err: any) {
      console.error("Save patient error:", err);
      const serverMessage =
        err?.response?.data?.message || err?.message || "Failed to save patient";
      setApiError(serverMessage);
      toast.error(serverMessage);
      if (serverMessage.toLowerCase().includes("nhs number")) {
        setErrors((prev) => ({ ...prev, nhs_number: serverMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500">
        <button
          onClick={() => navigate("/patients")}
          className="hover:text-gray-900 transition-colors"
        >
          Patients
        </button>
        <span className="text-gray-400">/</span>
        {isEdit && id && (
          <>
            <button
              onClick={() => navigate(`/patients/${id}`)}
              className="hover:text-gray-900 transition-colors"
            >
              {form.forename || form.surname ? `${form.forename} ${form.surname}`.trim() : "Profile"}
            </button>
            <span className="text-gray-400">/</span>
          </>
        )}
        <span className="text-gray-900 font-medium">{isEdit ? "Edit Patient" : "Add New Patient"}</span>
      </nav>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 space-y-5">
          {apiError && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 font-medium">
              {apiError}
            </div>
          )}

          {/* Patient Details */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User size={16} className="text-primary-600" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Personal Information
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {isEdit && (
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID</label>
                  <input
                    value={id || ""}
                    disabled
                    readOnly
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-lg text-sm text-gray-500 cursor-not-allowed"
                  />
                </div>
              )}
              <FormField
                label="First Name"
                name="forename"
                value={form.forename}
                onChange={handleChange}
                error={errors.forename}
                required
                placeholder="e.g. John"
              />
              <FormField
                label="Last Name"
                name="surname"
                value={form.surname}
                onChange={handleChange}
                error={errors.surname}
                required
                placeholder="e.g. Doe"
              />
              <FormField
                label="NHS Number"
                name="nhs_number"
                value={form.nhs_number}
                onChange={handleChange}
                error={errors.nhs_number}
                required
                placeholder="e.g. 123 456 7890"
              />
              <FormField
                label="Date of Birth"
                name="date_of_birth"
                value={form.date_of_birth}
                onChange={handleChange}
                error={errors.date_of_birth}
                required
                type="date"
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender <span className="text-red-500 ml-0.5">*</span>
                </label>
                <CustomSelect
                  value={form.gender}
                  onChange={(val) => {
                    setForm((prev) => ({ ...prev, gender: val }));
                    if (errors.gender) {
                      setErrors((prev) => {
                        const next = { ...prev };
                        delete next.gender;
                        return next;
                      });
                    }
                    setApiError(null);
                  }}
                  options={[
                    { value: "", label: "Select gender" },
                    { value: "Male", label: "Male" },
                    { value: "Female", label: "Female" },
                    { value: "Other", label: "Other" },
                  ]}
                  placeholder="Select gender"
                  error={!!errors.gender}
                />
                {errors.gender && <p className="text-xs text-red-600 mt-1">{errors.gender}</p>}
              </div>
              <FormField
                label="Hospital Number"
                name="hospital_number"
                value={form.hospital_number}
                onChange={handleChange}
                error={errors.hospital_number}
                required
                placeholder="Enter hospital number"
              />
              <FormField
                label="MRN Number"
                name="mrn_number"
                value={form.mrn_number}
                onChange={handleChange}
                error={errors.mrn_number}
                required
                placeholder="Enter MRN number"
              />
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Contact Details */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Phone size={16} className="text-primary-600" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Contact Details
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Contact Number"
                name="contact_number"
                value={form.contact_number}
                onChange={handleChange}
                error={errors.contact_number}
                placeholder="e.g. 07700 900000"
              />
              <FormField
                label="Email"
                name="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                type="email"
                placeholder="e.g. patient@email.com"
              />
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="Enter full address"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>
            </div>
          </div>

          <hr className="border-gray-100" />

          {/* Clinical Information */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Activity size={16} className="text-primary-600" />
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                Clinical Details
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <FormField
                label="Primary Diagnosis / Condition"
                name="diagnosis"
                value={form.diagnosis}
                onChange={handleChange}
                placeholder="e.g. Diffuse Large B-Cell Lymphoma (DLBCL)"
              />
            </div>
          </div>

          {/* Actions */}
          <hr className="border-gray-100" />
          <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3">
            <button
              type="button"
              onClick={handleReset}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={16} />
              Reset
            </button>
            <button
              type="button"
              onClick={() => navigate(isEdit && id ? `/patients/${id}` : "/patients")}
              disabled={loading}
              className="px-5 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors w-full sm:w-auto"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-primary-600 text-white px-5 py-2 rounded-lg text-sm font-medium shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  {isEdit ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? "Update Patient" : "Create Patient"}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

// --- Helper Component ---

const FormField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  type = "text",
  placeholder,
  className,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  error?: string;
  required?: boolean;
  type?: string;
  placeholder?: string;
  className?: string;
}) => (
  <div className={className}>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
        error ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

export default PatientForm;
